import {
	Box,
	Flex,
	Stack,
	Icon,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	Text,
} from '@chakra-ui/core';
import {getDisplayValues} from 'constants/dataDisplayValues';
import React, {useState} from 'react';
import numeral from 'numeral';
import {yellow} from 'styles/constants';
import Hybrid from '../components/icons/Hybrid';
import Globe from '../components/icons/Globe';
import Rocket from '../components/icons/Rocket';

const icons = {
	hybrid: <Hybrid width={50} height={50} />,
	globe: <Globe width={50} height={50} />,
	rocket: <Rocket width={50} height={50} />,
};

export const formatCurrency = (n) => {
	if (typeof n !== 'number') return;
	return numeral(n).format('$0,0');
};
export const baseFormat = (n) => {
	if (typeof n !== 'number') return;
	return numeral(n).format('0,0.00');
};

export const baseFormatClean = (n) => {
	if (typeof n !== 'number') return;
	return numeral(n).format('0,0');
};

export const parseDisplayValueInit = (calculatedValues) => {
	return (item) => {
		const value = calculatedValues[item.key];
		const unit = item.unit;

		// used when tables show "spacer" rows
		if (typeof value !== 'number') return null;

		switch (item.unit) {
			case '$':
			case '$/h':
				return formatCurrency(value);
			case 'tonnes':
			case 'litres':
			case 'times':
				return `${baseFormatClean(value)} ${unit}`;
			case 'km':
				return `${baseFormatClean(value)} ${unit}`;
			case 'months':
				return `${baseFormatClean(value)} ${unit}`;
			case 'h':
				return baseFormat(value) + ' ' + unit;
			default:
				return `${calculatedValues[item.key]} ${unit}`;
		}
	};
};

const ModelDetailsTable = ({calculatedValues}) => {
	const parseDisplayValue = parseDisplayValueInit(calculatedValues);

	const [rowVisibility, setRowVisibility] = useState({0: false});

	const Row = ({item, toggleButton, info, ...props}) => {
		return (
			<Flex
				direction={{base: 'column', lg: 'row'}}
				w={'100%'}
				my={2}
				fontSize={'22px'}
				fontWeight={'bold'}
				{...props}
			>
				{!!item.icon && (
					<Box minWidth={60} width={60} mr={{base: 0, lg: 2}}>
						{icons[item.icon]}
					</Box>
				)}
				<Box w={{base: '100%', lg: 7 / 12}} mr={{base: 0, lg: 4}}>
					<Flex direction="row">
						<Box>{item.displayText}</Box>
						{!!info && (
							<Popover trigger="hover" placment="bottom">
								<PopoverTrigger>
									<Flex
										alignItems="center"
										justifyContent="center"
										width={36}
										height={36}
									>
										<Icon name="info" size={18} />
									</Flex>
								</PopoverTrigger>
								<PopoverContent bg="#333" borderColor="#333">
									<PopoverBody>
										<Text
											color="white"
											fontSize={13}
											fontWeight={400}
											dangerouslySetInnerHTML={{__html: info}}
										/>
									</PopoverBody>
								</PopoverContent>
							</Popover>
						)}
					</Flex>
				</Box>
				<Flex
					alignContent={'center'}
					justifyContent={{base: 'flex-start', lg: 'flex-end'}}
					w={{base: '100%', lg: 4 / 12}}
					mr={{base: 0, lg: 4}}
					my={{base: 2, lg: 0}}
				>
					{item.key && item.unit ? parseDisplayValue(item) : null}
				</Flex>
				<Flex w={{base: '100%', lg: 1 / 12}} alignItems="center">
					{toggleButton}
				</Flex>
			</Flex>
		);
	};

	const ToggleBtn = ({rowNumber}) => {
		const rowVisible = rowVisibility[rowNumber];
		return (
			<button
				className={rowVisible ? 'btn-full' : 'btn-skeleton'}
				onClick={() => toggleRow(rowNumber)}
				style={{
					whiteSpace: 'nowrap',
					overflow: 'hidden',
				}}
			>
				{rowVisible ? 'Show less' : 'Show more'}
			</button>
		);
	};

	const toggleRow = (n) => {
		setRowVisibility({...rowVisibility, [n]: !rowVisibility[n]});
	};

	const rowClass = (visibility, n) => (visibility[n] ? 'show' : 'hide');

	return (
		<Box w={'100%'} ml={{base: 0, lg: 4}} bg={yellow} p={6}>
			<Stack className="results">
				<Row
					item={getDisplayValues('Production increase ($)')}
					toggleButton={<ToggleBtn rowNumber={0} />}
					info={`Instead of traveling on tracks, your excavator can be transported much faster in E Series dollies. The saved time can be used for productive work, allowing the loading to be planned more efficiently.<br><br>Production values are taken from industry averages and calculated from them. For more detailed adjustments, you can change the values from "Adjust Values".`}
				/>
				<Stack className={rowClass(rowVisibility, 0)}>
					{[
						getDisplayValues('Annual increase in production (tonnes)'),
						getDisplayValues('Utilization without Sleipner (%)'),
						getDisplayValues('Utilization with Sleipner (%)'),
					].map((item, index) => {
						return (
							<Row fontWeight={'100'} fontSize={'18px'} key={item + index} item={item} />
						);
					})}
				</Stack>
				<Row
					item={getDisplayValues('Annual undercarriage rebuild part savings ($)')}
					toggleButton={<ToggleBtn rowNumber={1} />}
				/>
				<Stack className={rowClass(rowVisibility, 1)}>
					{[
						getDisplayValues('Lifetime undercarriage rebuild savings'),
						getDisplayValues(
							'Undercarriage related labour hours saving during lifetime ($)',
						),
						getDisplayValues(
							'Undercarriage related labour hours saving during lifetime (h)',
						),
					].map((item, index) => {
						return (
							<Row fontWeight={'100'} fontSize={'18px'} key={item + index} item={item} />
						);
					})}
				</Stack>
				<Row
					item={getDisplayValues('LCC cost reduction (%)', 'LCC reduction percentage')}
					toggleButton={<ToggleBtn rowNumber={2} />}
				/>
				<Stack className={rowClass(rowVisibility, 2)}>
					{[
						getDisplayValues(
							'LCC cost reduction ($/h)',
							'Hourly life cycle cost rection ($/h)',
						),
					].map((item, index) => {
						return (
							<Row fontWeight={'100'} fontSize={'18px'} key={item + index} item={item} />
						);
					})}
				</Stack>
				<Row
					item={getDisplayValues('Annual saved travelling time (h)')}
					toggleButton={<ToggleBtn rowNumber={3} />}
					info="Wheels under excavators bring flexibility to your operations, saving a big portion of time. Heavy machinery can be effortlessly moved and relocated whenever needed, even in the middle of a shift.<br><br>The amount of tramming can be transferred into productive work. With Sleipners we can minimize the travelling time and put the machinery into actual work."
				/>
				<Stack className={rowClass(rowVisibility, 3)}>
					{[
						getDisplayValues('Annual time spent travelling without Sleipner (h)'),
						getDisplayValues(
							'Annual time spent travelling with Sleipner (h)',
							'Annual time spent travelling with Sleipner (h)',
						),
						getDisplayValues(
							'Annual time spent travelling with Sleipner (%)',
							'Annual time spent travelling with Sleipner (%)',
						),
					].map((item, index) => {
						return (
							<Row
								fontWeight={'100'}
								fontSize={'18px'}
								item={item}
								key={item.key + index}
							/>
						);
					})}
				</Stack>
				<Row
					item={getDisplayValues('Fuel saved with Sleipner in year (litres)')}
					toggleButton={<ToggleBtn rowNumber={4} />}
					info="Tramming is a consumptive way to travel with excavator. It costs the machine and fuel. When travelled with Sleipner, the excavator runs on idling and consumes a fracture of time and fuel compared to tramming."
				/>
				<Stack className={rowClass(rowVisibility, 4)}>
					{[
						getDisplayValues('Fuel saved with Sleipner during lifetime (litres)'),
						getDisplayValues('Yearly fuel savings ($)'),
						getDisplayValues('Lifetime fuel savings ($)'),
					].map((item, index) => {
						return (
							<Row fontWeight={'100'} fontSize={'18px'} key={item + index} item={item} />
						);
					})}
				</Stack>
				<Row
					item={getDisplayValues('CO2 reduction with Sleipner in year (tonnes)')}
					toggleButton={<ToggleBtn rowNumber={5} />}
				/>
				<Stack className={rowClass(rowVisibility, 5)}>
					{[getDisplayValues('CO2 reduction with Sleipner in lifetime (tonnes)')].map(
						(item, index) => {
							return (
								<Row
									fontWeight={'100'}
									fontSize={'18px'}
									key={item + index}
									item={item}
								/>
							);
						},
					)}
					<Text fontSize={13} my={2}>
						{
							'* Check "Fun Facts" for more info on how much Sleipner actually lowers the CO2 emissions'
						}
					</Text>
				</Stack>
				<Row item={getDisplayValues('Investment payback time (month)')} />
				<Row
					item={{displayText: 'Fun Facts'}}
					toggleButton={<ToggleBtn rowNumber={6} />}
				/>
				<Stack className={rowClass(rowVisibility, 6)}>
					{[
						getDisplayValues(
							'Lifetime CO2 kilometers compared to low emission hybrid cars',
						),
						getDisplayValues('The amount of times you can drive around the world'),
						getDisplayValues('The amount of times to drive to moon and back'),
					].map((item, index) => {
						return (
							<Row fontWeight={'100'} fontSize={'18px'} key={item + index} item={item} />
						);
					})}
				</Stack>
				{/* <Row
					item={getDisplayValues('Undercarriage yearly savings')}
				/> */}
			</Stack>
		</Box>
	);
};

export default ModelDetailsTable;
